import {
  action, makeAutoObservable, observable, observe, when, computed,
} from 'mobx';
import {
  UserFreeShipping, FreeShippingCodes, FreeShippingCodesStatuses, AppFreeShipping,
} from '@gamiphy/bl/freeShipping';
import { FreeShippingApi } from '@gamiphy/gamix-modules/freeShipping';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';

class FreeShippingStore {
    @observable loading = false;

    @observable currentUserShipping?: UserFreeShipping = undefined;

    @observable shippingCodes: FreeShippingCodes[] = [];

    @observable userFreeShipping?: UserFreeShipping[] = undefined;

    @observable appFreeShipping?: AppFreeShipping[] = undefined;

    constructor() {
      makeAutoObservable(this);

      void this.initialize();

      observe(UserStore, 'user', () => {
        void this.loadUserShippingInfo();
      });
    }

    @action async initialize(): Promise<void> {
      await when(() => AppStore.initialized && UserStore.isLoggedIn);
      if (AppStore.app.agent === 'vtex') {
        this.appFreeShipping = await FreeShippingApi.getAppFreeShippingList();
        if (this.checkAppLevelFreeShipping(LevelStore.currentTier?.id ?? '')) {
          this.userFreeShipping = await FreeShippingApi.getUserFreeShippingList();
        }
        this.shippingCodes = await FreeShippingApi.getFreeShippingCodes();
      }

      await when(() => !!UserStore.user);
      await this.loadUserShippingInfo();
    }

    @action
    async loadUserShippingInfo() {
      if (UserStore.user) {
        this.loading = true;

        if (AppStore.app.agent === 'vtex' && this.checkAppLevelFreeShipping(LevelStore.currentTier?.id ?? '')) {
          try {
            this.currentUserShipping = await FreeShippingApi.getCurrentUserFreeShipping();
            this.userFreeShipping?.push(this.currentUserShipping);
          } catch {
            this.currentUserShipping = undefined;
            this.userFreeShipping = [];
          }
        }

        this.loading = false;
      }
    }

    @action
    async generateCode(): Promise<void> {
      if (UserStore.user) {
        await FreeShippingApi.redeemFreeShippingCode();
        this.shippingCodes = await FreeShippingApi.getFreeShippingCodes();
      }
    }

    @computed canGenerateCode(): boolean {
      return !!this.activeCode || this.remainingNumberOfCodes > 0;
    }

    @computed checkLevelFreeShipping(tierId: string): boolean {
      return this.userFreeShipping?.map((free) => free.tier).includes(tierId) ?? false;
    }

    @computed checkAppLevelFreeShipping(tierId: string): boolean {
      return this.appFreeShipping?.map((free) => free.tier).includes(tierId) ?? false;
    }

    @computed lastNotValidCode(userFreeShippingId: string): FreeShippingCodes | undefined {
      const reversedShippingCodes = [...this.shippingCodes].reverse();
      return reversedShippingCodes.find((code) => code.status !== FreeShippingCodesStatuses.valid && code.userFreeShipping === userFreeShippingId);
    }

    @computed get activeCode(): FreeShippingCodes | undefined {
      return this.shippingCodes.find((code) => code.status === FreeShippingCodesStatuses.valid);
    }

    @computed get remainingNumberOfCodes(): number {
      return this.currentUserShipping?.remainingNumberOfCodes ?? 0;
    }
}

export default new FreeShippingStore();
